// OP-3605 — Minhas solicitações (ESL2): o que você pediu e em que pé está.
// A leitura é a reduzida do solicitante (sem serial, praça ou responsável).
// OP-3619 — no celular a tabela vira cartão e "Nova solicitação" vira botão
// flutuante, acima da barra de abas.
import { FC, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useEstoqueMinhasSolicitacoes } from '../../../../hooks/estoque'
import { MinhaSolicitacaoRow, TIPO_DEMANDA_LABEL } from '../../../../types/estoque'
import {
  BD, SEVERIDADE, Button, CaixaLista, Card, ListaCartoes, PageSubtitle, PageTitle, SoDesktop, Spinner,
  StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate, fmtDateTime } from '../../statusUi'
import { Cartao } from '../Cartao'
import { FabLink } from '../FabLink'
import { plural, prazoUi, textoRegime } from '../formato'
import { STATUS_SOLICITACAO_UI, ehFinal } from '../statusSolicitacao'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const COLS = 7

export const EstoqueMinhas: FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  // `total` aqui é só o tamanho da página: a rota devolve a lista cortada em
  // 50, sem contagem geral. A tela nunca escreve "N pedidos no total".
  const { rows, lastPage, isLoading, isError, isUnauthorized } = useEstoqueMinhasSolicitacoes(page)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const esperandoVoce = rows.filter((r) => r.status === 'AGUARDANDO_INFO')
  const badge = (r: MinhaSolicitacaoRow) => {
    const ui = STATUS_SOLICITACAO_UI[r.status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    return <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>
  }
  const linha = (r: MinhaSolicitacaoRow) => (
    <tr key={r.id} onClick={() => navigate(`/interno/estoque/minhas/${r.id}`)} style={{ cursor: 'pointer', opacity: ehFinal(r.status) ? 0.7 : 1 }}>
      <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.numero}</td>
      <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(r.criadoEm)}</td>
      <td>{TIPO_DEMANDA_LABEL[r.tipoDemanda] ?? r.tipoDemanda}</td>
      <td>{r.detentorNome ?? '—'}<div style={{ fontSize: 12, color: BD.grayText }}>{r.eventoNomes ?? ''}</div></td>
      <td style={{ fontSize: 13 }} title="Peças (com série) e unidades de consumível não se somam">
        {textoRegime({ pecas: r.pecasPedidas, consumo: r.consumoPedido })}
        <div style={{ fontSize: 12, color: BD.grayText }}>{plural(Number(r.linhas) || 0, 'item', 'itens')}</div>
      </td>
      <td>{fmtDate(r.prazoEm)}</td>
      <td>{badge(r)}{r.status === 'AGUARDANDO_INFO' && <span style={{ marginLeft: 6, color: SEVERIDADE.atencao.texto, fontSize: 12, fontWeight: 600 }}>responda</span>}</td>
    </tr>
  )

  const cartao = (r: MinhaSolicitacaoRow) => {
    const ui = STATUS_SOLICITACAO_UI[r.status] ?? { bg: '#F3F4F6', color: '#374151', label: r.status }
    const encerrado = ehFinal(r.status)
    const responda = r.status === 'AGUARDANDO_INFO'
    const prazo = prazoUi(r.prazoEm)
    const alerta = !encerrado && prazo.alerta
    return (
      <Cartao
        key={r.id}
        to={`/interno/estoque/minhas/${r.id}`}
        codigo={r.numero}
        badge={ui}
        titulo={r.detentorNome ?? '—'}
        esmaecido={encerrado}
        destaque={responda || alerta}
        meta={(
          <>
            {TIPO_DEMANDA_LABEL[r.tipoDemanda] ?? r.tipoDemanda}
            {r.eventoNomes ? ` · ${r.eventoNomes}` : ''}
            <div>
              {textoRegime({ pecas: r.pecasPedidas, consumo: r.consumoPedido })}
              {' · '}
              {plural(Number(r.linhas) || 0, 'item', 'itens')}
            </div>
          </>
        )}
        rodape={responda ? (
          <span style={{ color: SEVERIDADE.atencao.texto, fontWeight: 600 }}>A logística perguntou algo — responda</span>
        ) : (
          <span style={{ color: alerta ? SEVERIDADE.atencao.texto : BD.grayText, fontWeight: alerta ? 600 : 400 }}>
            {encerrado ? `Aberta em ${fmtDate(r.criadoEm)}` : prazo.texto}
          </span>
        )}
      />
    )
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Minhas solicitações</PageTitle>
          <PageSubtitle>Tudo que você pediu, e em que pé está. Quem separa, envia e confere é a Logística; aqui você acompanha e responde o que for perguntado.</PageSubtitle>
        </div>
        <SoDesktop>
          <Button onClick={() => navigate('/interno/estoque/solicitar')}>Nova solicitação</Button>
        </SoDesktop>
      </div>

      {esperandoVoce.length > 0 && (
        <Card style={{ padding: 12, marginBottom: 16, borderColor: SEVERIDADE.atencao.borda }}>
          <b>Esperando você:</b> a logística perguntou algo em {plural(esperandoVoce.length, 'pedido', 'pedidos')}. Abra para responder.
        </Card>
      )}

      <CaixaLista>
        <TabelaWrap>
          <Table>
            <thead>
              <tr><th>Número</th><th>Aberta em</th><th>Tipo</th><th>Para quem / eventos</th><th>Itens</th><th>Prazo</th><th>Status</th></tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>Não foi possível carregar as suas solicitações.</td></tr>}
              {!isLoading && !isError && rows.length === 0 && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>
                  {page === 1 ? 'Você ainda não abriu nenhuma solicitação.' : 'Não há mais pedidos.'}
                </td></tr>
              )}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && <Card style={{ padding: 20, color: BD.red }}>Não foi possível carregar as suas solicitações.</Card>}
          {!isLoading && !isError && rows.length === 0 && (
            <Card style={{ padding: 20, color: BD.grayText }}>
              {page === 1 ? 'Você ainda não abriu nenhuma solicitação.' : 'Não há mais pedidos.'}
            </Card>
          )}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {page}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => setPage(page + 1)}>Próxima ›</Button>
        </div>
      </CaixaLista>

      <FabLink to="/interno/estoque/solicitar" rotulo="Nova solicitação" />
    </>
  )
}

export default EstoqueMinhas
